import loadable from '@loadable/component';
import { Stack, Typography } from '@mui/material';
import { Form, Formik, type FormikHelpers } from 'formik';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { array, object, string } from 'yup';
import type { Schema } from 'yup';

import AlertOptionList from '@components/ConfigOptionList/AlertOptionList';
import DrawerListener from '@components/Drawer';
import HorizontalRule from '@components/HorizontalRule';
import IconButton from '@components/IconButton';

import { useVerifyAlertCode } from '@hooks/api/alerts/alert_recipient_setting';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import {
  VerifyDeliveryMethod as AlertDeliveryMethod,
  AlertRecipientType,
} from 'generated/types';

import AlertVerification from './AlertVerification';

const messages = defineMessages({
  instructions: {
    id: 'alertRecipientSettingForm.instructions.default',
    defaultMessage:
      "Select your {isOrg, select, true{organization's} other{}} out-of-parameter alert delivery method below. You can enable email, sms, or both. Alerts are created within a patient's alert section and, when triggered, will be sent to the destination(s) you have turned on here:",
  },
  title: {
    id: 'alertRecipientSettingForm.title.default',
    defaultMessage: 'Receive alerts via',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'Required',
  },
  drawerTitle: {
    id: 'drawer.alertRecipient.title',
    defaultMessage:
      'Setup {isOrg, select, true{Org} other{User}} Alert Recipient',
  },
});

export interface FormValues {
  method: AlertDeliveryMethod | null;
  code: string[];
  destination: string;
}

interface AlertRecipientSettingFormProps {
  /** Recipient type */
  type: AlertRecipientType;
  /** If type is account, then id is account_id. If type is user, then id is user_id */
  id: string;
  /** Should this form be view only? Used to view a user's org settings in manage account */
  viewOnly?: boolean;
  /** Custom text to be displayed in component */
  overridingText?: {
    instructions?: string;
    title?: string;
    subtitle?: string;
  };
}

export default function AlertRecipientSettingForm({
  id,
  type,
  viewOnly,
  overridingText,
}: AlertRecipientSettingFormProps) {
  const { formatMessage } = useIntl();
  const { phoneSchema } = useCommonSchemas();
  const [openForm, setOpenForm] = useState<boolean>(false);

  const [verifyAlertCode] = useVerifyAlertCode();

  const validationSchema: Schema<FormValues> = object().shape({
    method: string()
      .oneOf([AlertDeliveryMethod.email, AlertDeliveryMethod.sms])
      .required(formatMessage(messages.required)),
    code: array()
      .of(string().required(formatMessage(messages.required)))
      .required(),
    destination: string()
      .required()
      .when('method', {
        is: (value: AlertDeliveryMethod | null | undefined) =>
          value === AlertDeliveryMethod.sms,
        then: () => phoneSchema.required(formatMessage(messages.required)),
        otherwise: () =>
          string().email().required(formatMessage(messages.required)),
      }),
  });

  const handleOpenForm = () => setOpenForm(true);
  const handleCloseForm = () => setOpenForm(false);

  const initialValues: FormValues = {
    method: null,
    destination: '',
    code: ['', '', '', '', '', ''],
  };

  const handleSubmit = async (
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>
  ) => {
    if (!id || !values.destination) {
      throw new Error('No user or destination');
    }

    const { errors } = await verifyAlertCode({
      code: values.code.join(''),
      destination: values.destination,
      ...(type === 'account' && {
        account_id: id,
      }),
      ...(type === 'user' && {
        user_id: id,
      }),
    });
    if (!errors) {
      resetForm();
      handleCloseForm();
    }
  };

  const AlertRecipientDrawerContent = loadable(
    () => import('@components/Drawer/Content/Alert/AlertRecipientDrawer'),
    { ssr: false }
  );

  return (
    <Stack>
      {!viewOnly && (
        <Typography variant="caption">
          {overridingText?.instructions ??
            formatMessage(messages.instructions, {
              isOrg: type === 'account',
            })}
        </Typography>
      )}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ resetForm, setFieldValue, values }) => {
          const handleDeliveryMethodChange = (value: AlertDeliveryMethod) => {
            resetForm();
            setFieldValue('method', value);
            handleOpenForm();
          };

          return (
            <Form>
              <Stack direction="row">
                <Typography variant="body1">
                  {overridingText?.title ?? formatMessage(messages.title)}
                </Typography>
                <DrawerListener
                  content={<AlertRecipientDrawerContent type={type} />}
                  title={formatMessage(messages.drawerTitle, {
                    isOrg: type === AlertRecipientType.account,
                  })}
                >
                  {({ openDrawer }) => (
                    <IconButton
                      color="info.main"
                      iconType="fal"
                      name="circle-info"
                      onClick={openDrawer}
                    />
                  )}
                </DrawerListener>
              </Stack>

              {overridingText?.subtitle && (
                <Typography variant="caption">
                  {overridingText.subtitle}
                </Typography>
              )}

              <AlertOptionList
                id={id}
                onClick={handleDeliveryMethodChange}
                method={values.method}
                type={type}
                viewOnly={viewOnly}
              />
              {openForm && (
                <>
                  <HorizontalRule />
                  <AlertVerification />
                </>
              )}
            </Form>
          );
        }}
      </Formik>
    </Stack>
  );
}
